import { Box } from '@everlywell/ui-kit';
import React from 'react';
import { Autoplay, FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Card, { CardProps } from '../Card';

// Types ----------

export interface CardCarouselProps {
  cards: CardProps[];
  // If autoScroll is set to 'off', the carousel will not auto scroll. Default is 'on'.
  autoScroll?: 'on' | 'off';
  //  If "displayMode" is set to "straight", the cards will be displayed in a straight line without any padding. Otherwise, the cards will be displayed in a wave pattern.
  displayMode?: 'default' | 'straight';
}

// CardCarousel ------

const CardCarousel: React.FC<CardCarouselProps> = (props) => {
  const { cards, autoScroll = 'on', displayMode = 'default' } = props;

  let cardList: CardProps[] = cards;

  // If there are less than 7 cards, duplicate the cards to create a continuous loop
  if (cards.length <= 7) {
    cardList = [...cards, ...cards];
  }

  const setPadding = (
    displayMode: CardCarouselProps['displayMode'],
    index: number,
  ) => {
    if (displayMode === 'straight') {
      return {
        paddingBottom: 0,
        paddingTop: 0,
      };
    }

    return {
      paddingTop: index % 2 === 0 ? 8 : 0,
      paddingBottom: index % 2 !== 0 ? 8 : 0,
    };
  };

  const slides = cardList.map((card, index) => (
    <SwiperSlide
      data-testid="hero-carousel-swiper-card"
      key={`hero-carousel-swiper-card-${index}`}
      style={{ width: '256px' }}
    >
      <Box width="100%" {...setPadding(displayMode, index)}>
        <Card {...card} marginRight="16px" />
      </Box>
    </SwiperSlide>
  ));

  return (
    <Box
      width="100%"
      sx={{
        '.swiper-wrapper': {
          transitionTimingFunction: 'linear',
        },
      }}
    >
      <Swiper
        slidesPerView="auto"
        loop={true}
        speed={10000} // 10s, speed calculated from Figma's prototype
        grabCursor={true}
        freeMode={true}
        data-testid="hero-carousel-swiper-wrapper"
        // Delay 1 to start the auto scroll immediately.
        // If the delay is 0, the auto scroll will try to start before the swiper is fully loaded and will not work.
        autoplay={
          autoScroll === 'on'
            ? {
                delay: 1,
                disableOnInteraction: true,
                pauseOnMouseEnter: false, // Setting it to false for now, pending confirmation by design
              }
            : false
        }
        modules={[FreeMode, Autoplay]}
        style={{
          paddingTop: '16px',
        }}
      >
        {slides}
      </Swiper>
    </Box>
  );
};

// Export ---------

export default React.memo(CardCarousel);
